import { TextLinkWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';
import { ChangeEvent } from 'react';
import { Messages } from '../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../commonInfra/components/FormikCheckbox';
import { FormikTextBox } from '../../../../commonInfra/components/FormikField';
import { HelpService } from '../../../../commonInfra/services/HelpService';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { formError, learnMoreStyle } from '../../../styles/CommonStyles';
import { useCompositeValidator, usePositiveIntegerValidator, useRangeValidator } from '../../utils';
import { SiteSettingsForServerAdminProps } from '../site-settings/ReactSiteSettingsForServerAdmin';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

export type AutoSuspendExtractRefreshSectionProps = SiteSettingsForServerAdminProps<'autoSuspendRefreshEnabled' | 'autoSuspendRefreshInactivityWindow'>

export type AutoSuspendSectionDeps = {
  helpService: HelpService;
}

export const AUTO_SUSPEND_EXTRACT_REFRESH_FIELD = 'settings.autoSuspendRefreshEnabled';
export const AUTO_SUSPEND_WINDOW_FIELD = 'settings.autoSuspendRefreshInactivityWindow';
export const AUTO_SUSPEND_MIN_RANGE_VALUE = '7';
export const AUTO_SUSPEND_MAX_RANGE_VALUE = '100';

export function InternalAutoSuspendExtractRefreshSection(props: AutoSuspendExtractRefreshSectionProps & AutoSuspendSectionDeps) {
  const formik = useFormikContext<AutoSuspendExtractRefreshSectionProps>();

  const validate = useCompositeValidator(
    usePositiveIntegerValidator(() => Messages.extractRefreshAutoSuspendInactivityWindowRangeInvalid({MIN_RANGE_VALUE: AUTO_SUSPEND_MIN_RANGE_VALUE,MAX_RANGE_VALUE: AUTO_SUSPEND_MAX_RANGE_VALUE})),
    useRangeValidator(
      7, () => Messages.extractRefreshAutoSuspendInactivityWindowRangeInvalid({MIN_RANGE_VALUE: AUTO_SUSPEND_MIN_RANGE_VALUE,MAX_RANGE_VALUE: AUTO_SUSPEND_MAX_RANGE_VALUE}),
      100, () => Messages.extractRefreshAutoSuspendInactivityWindowRangeInvalid({MIN_RANGE_VALUE: AUTO_SUSPEND_MIN_RANGE_VALUE,MAX_RANGE_VALUE: AUTO_SUSPEND_MAX_RANGE_VALUE})
    )
  );

  const changeHandler = React.useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    formik.setFieldValue(AUTO_SUSPEND_WINDOW_FIELD, value !== '' && !isNaN(+value) ? +value : value);
  }, [formik.setFieldValue]);

  return !FeatureFlags.extractRefreshPauseEnabled ? null : (
    <SettingsSection title={Messages.extractRefreshAutoSuspendGroupName()}
                     description={renderDescription(props.helpService)}>
      <FormikCheckbox name={AUTO_SUSPEND_EXTRACT_REFRESH_FIELD}
                      label={Messages.extractRefreshAutoSuspend()}
                      testId={'extract-refresh-auto-pause-enabled-checkbox'}>
        <div>
          <FormikTextBox name={AUTO_SUSPEND_WINDOW_FIELD}
                      maxLength={3}
                      label={Messages.extractRefreshAutoSuspendInactivityWindowLabel()}
                      testId={'extract-refresh-auto-pause-enabled-numberbox'}
                      showValidationMessage={false}
                      onChange={changeHandler}
                      validate={validate}/>

        </div>
        {
          formik.errors?.settings?.autoSuspendRefreshInactivityWindow &&
          <div className={formError} db-test-data-id={'auto-suspend-refresh-invalid-number-range'}>
            {formik.errors.settings.autoSuspendRefreshInactivityWindow}
          </div>
        }

      </FormikCheckbox>
    </SettingsSection>
  );
}

function renderDescription(helpService: HelpService) {
  return (
    <>
      <span>{Messages.extractRefreshAutoSuspendMsg()}</span>
      <TextLinkWidget target={'_blank'}
                      testId={'auto-suspend-learn-more'}
                      className={learnMoreStyle}
                      href={helpService.getServerHelpUrlByCategory('extract-auto-suspend')}>
        {Messages.learnMore()}
      </TextLinkWidget>
    </>
  );
}

export function AutoSuspendExtractRefreshSection(props: AutoSuspendExtractRefreshSectionProps) {
  return <InternalAutoSuspendExtractRefreshSection helpService={HelpService} {...props}/>;
}

export const AutoSuspendExtractRefreshSectionSpecs = componentRegistryEntry({
  name: 'AutoSuspendExtractRefreshSection',
  component: AutoSuspendExtractRefreshSection
});
